import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store';
import { logout } from '../store/authSlice';
import SettingsKnob from '../components/common/SettingsKnob';
import Sidebar from '../components/common/Sidebar';
import Toggle from '../components/dashboard/Toggle';
import { settingsApi, handleSettingsError, InterviewSettingsData } from '../api/settingsApi';
import { voiceAgentApi } from '../api/voiceAgentApi';
import { VOICE_AGENT_BUILD_ENABLED } from '../utils/constants';
import './CandidateProfile.css';
import { vibeService } from '../services/VibeService';
import { getOrgDepartmentLabels, onboardingApi } from '../api/onboardingApi';
import './Settings.css';
import SettingsAutoSaveWidget from '../components/settings/SettingsAutoSaveWidget';

const AUTO_SAVE_DELAY_MS = 5000;

type SettingsState = 'loading' | 'ready' | 'saving' | 'error';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const [organizationName, setOrganizationName] = useState<string | undefined>();
  const [departmentName, setDepartmentName] = useState<string | undefined>();

  const [settingsState, setSettingsState] = useState<SettingsState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Settings data
  const [settingsData, setSettingsData] = useState<InterviewSettingsData | null>(null);
  const [difficulty, setDifficulty] = useState<string>('medium');
  const [experienceFocus, setExperienceFocus] = useState<string>('varied');
  const [mode, setMode] = useState<string>('professional');
  const [responseFeedbackEnabled, setResponseFeedbackEnabled] = useState<boolean>(true);
  const [vibeGifsEnabled, setVibeGifsEnabled] = useState<boolean>(false);
  const [useVoiceAgentEnabled, setUseVoiceAgentEnabled] = useState<boolean>(true);
  const [accent, setAccent] = useState<string>('en-IN');
  const [interviewerGender, setInterviewerGender] = useState<string>('female');
  const [voiceAgentAvailable, setVoiceAgentAvailable] = useState<boolean>(VOICE_AGENT_BUILD_ENABLED);

  // Track if settings have been modified
  const [hasChanges, setHasChanges] = useState(false);
  const [autoSaveTimerKey, setAutoSaveTimerKey] = useState(0);

  const markDirty = useCallback(() => {
    setHasChanges(true);
    setAutoSaveTimerKey((k) => k + 1);
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      setSettingsState('loading');
      vibeService.trigger('loading');
      setErrorMessage(null);
      const response = await settingsApi.getInterviewSettings();
      
      if (response.success && response.data) {
        setSettingsData(response.data);
        setDifficulty(response.data.settings.difficulty);
        setExperienceFocus(response.data.settings.experience_focus);
        setMode(response.data.settings.mode);
        setResponseFeedbackEnabled(response.data.settings.response_feedback ?? true);
        setVibeGifsEnabled(response.data.settings.vibe_gifs_enabled ?? false);
        setUseVoiceAgentEnabled(response.data.settings.use_voice_agent ?? true);
        setAccent(response.data.settings.accent ?? 'en-IN');
        setInterviewerGender(response.data.settings.interviewer_gender ?? 'female');
        setHasChanges(false);
        setAutoSaveTimerKey((k) => k + 1);
        // Sync settings to localStorage for use in other components
        localStorage.setItem('response_feedback', String(response.data.settings.response_feedback ?? true));
        localStorage.setItem('vibe_gifs_enabled', String(response.data.settings.vibe_gifs_enabled ?? false));
        localStorage.setItem('use_voice_agent', String(response.data.settings.use_voice_agent ?? true));
        setSettingsState('ready');
        vibeService.dismiss();
      } else {
        throw new Error('Failed to load settings');
      }
    } catch (error) {
      const message = handleSettingsError(error);
      setErrorMessage(message);
      setSettingsState('error');
      vibeService.dismiss();
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    let cancelled = false;
    const probeVoiceAgent = async () => {
      if (VOICE_AGENT_BUILD_ENABLED) {
        setVoiceAgentAvailable(true);
        return;
      }
      try {
        const status = await voiceAgentApi.getStatus();
        if (!cancelled) {
          setVoiceAgentAvailable(Boolean(status?.enabled));
        }
      } catch {
        if (!cancelled) setVoiceAgentAvailable(false);
      }
    };
    probeVoiceAgent();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const loadOrgDept = async () => {
      try {
        const profileResponse = await onboardingApi.getUserProfile();
        if (cancelled) return;
        const orgDept = getOrgDepartmentLabels(profileResponse.data);
        setOrganizationName(orgDept.organizationName);
        setDepartmentName(orgDept.departmentName);
      } catch {
        if (!cancelled) {
          setOrganizationName(undefined);
          setDepartmentName(undefined);
        }
      }
    };
    loadOrgDept();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle setting changes
  const handleDifficultyChange = (value: string) => {
    setDifficulty(value);
    markDirty();
  };

  const handleExperienceFocusChange = (value: string) => {
    setExperienceFocus(value);
    markDirty();
  };

  const handleModeChange = (value: string) => {
    setMode(value);
    markDirty();
  };

  const handleResponseFeedbackChange = (checked: boolean) => {
    setResponseFeedbackEnabled(checked);
    localStorage.setItem('response_feedback', String(checked));
    markDirty();
  };

  const handleVibeGifsChange = (checked: boolean) => {
    setVibeGifsEnabled(checked);
    localStorage.setItem('vibe_gifs_enabled', String(checked));
    markDirty();
  };

  const handleUseVoiceAgentChange = (checked: boolean) => {
    setUseVoiceAgentEnabled(checked);
    localStorage.setItem('use_voice_agent', String(checked));
    markDirty();
  };

  const handleAccentChange = (value: string) => {
    setAccent(value);
    markDirty();
  };

  const handleInterviewerGenderChange = (value: string) => {
    setInterviewerGender(value);
    markDirty();
  };

  const isTechnicalMode = mode === 'technical';
  const femaleInterviewerLabel = isTechnicalMode ? 'Jacky' : 'Clara';
  const maleInterviewerLabel = isTechnicalMode ? 'Aditya' : 'Derek';

  // Save settings
  const handleSave = useCallback(async () => {
    try {
      setSettingsState('saving');
      setErrorMessage(null);
      setSuccessMessage(null);

      await settingsApi.updateInterviewSettings({
        difficulty,
        experience_focus: experienceFocus,
        mode,
        response_feedback: responseFeedbackEnabled,
        vibe_gifs_enabled: vibeGifsEnabled,
        use_voice_agent: useVoiceAgentEnabled,
        accent,
        interviewer_gender: interviewerGender,
      });

      setSettingsData((prev) =>
        prev
          ? {
              ...prev,
              settings: {
                ...prev.settings,
                difficulty: difficulty as InterviewSettingsData['settings']['difficulty'],
                experience_focus: experienceFocus as InterviewSettingsData['settings']['experience_focus'],
                mode: mode as InterviewSettingsData['settings']['mode'],
                response_feedback: responseFeedbackEnabled,
                vibe_gifs_enabled: vibeGifsEnabled,
                use_voice_agent: useVoiceAgentEnabled,
                accent: accent as 'en-IN' | 'en-US',
                interviewer_gender: interviewerGender as 'female' | 'male',
              },
            }
          : prev
      );

      setSuccessMessage('Settings saved successfully!');
      setHasChanges(false);
      setSettingsState('ready');
      vibeService.trigger('success');
    } catch (error) {
      const message = handleSettingsError(error);
      setErrorMessage(message);
      setSettingsState('ready');
      vibeService.trigger('error');
    }
  }, [
    difficulty,
    experienceFocus,
    mode,
    responseFeedbackEnabled,
    vibeGifsEnabled,
    useVoiceAgentEnabled,
    accent,
    interviewerGender,
  ]);

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (!hasChanges || settingsState !== 'ready') {
      return;
    }
    const id = window.setTimeout(() => {
      void handleSaveRef.current();
    }, AUTO_SAVE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [autoSaveTimerKey, hasChanges, settingsState]);

  // Reset to original settings
  const handleReset = () => {
    if (settingsData) {
      setDifficulty(settingsData.settings.difficulty);
      setExperienceFocus(settingsData.settings.experience_focus);
      setMode(settingsData.settings.mode);
      setResponseFeedbackEnabled(settingsData.settings.response_feedback ?? true);
      setVibeGifsEnabled(settingsData.settings.vibe_gifs_enabled ?? false);
      setUseVoiceAgentEnabled(settingsData.settings.use_voice_agent ?? true);
      setAccent(settingsData.settings.accent ?? 'en-IN');
      setInterviewerGender(settingsData.settings.interviewer_gender ?? 'female');
      // Sync settings to localStorage
      localStorage.setItem('response_feedback', String(settingsData.settings.response_feedback ?? true));
      localStorage.setItem('vibe_gifs_enabled', String(settingsData.settings.vibe_gifs_enabled ?? false));
      localStorage.setItem('use_voice_agent', String(settingsData.settings.use_voice_agent ?? true));
      setHasChanges(false);
      setAutoSaveTimerKey((k) => k + 1);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleRetry = () => {
    loadSettings();
  };

  const handleStartInterview = () => {
    navigate('/interview');
  };

  // Generate user info from email
  const emailName = user?.email?.split('@')[0] || 'user';
  const firstName = emailName.split(/[._]/)[0] || 'User';
  const initials = emailName.slice(0, 2).toUpperCase();

  // Display labels for settings options
  const difficultyLabels: Record<string, string> = {
    easy: 'Easy',
    medium: 'Medium',
    hard: 'Hard',
  };

  const experienceFocusLabels: Record<string, string> = {
    varied: 'Varied',
    recent_deep: 'Recent & Deep',
  };

  const modeLabels: Record<string, string> = {
    technical: 'Technical',
    behavioral: 'Behavioral',
    professional: 'Professional',
  };

  return (
    <div className="settings-page" style={{ display: 'flex' }}>
      <SettingsAutoSaveWidget
        visible={hasChanges && (settingsState === 'ready' || settingsState === 'saving')}
        saving={settingsState === 'saving'}
        timerKey={autoSaveTimerKey}
        durationMs={AUTO_SAVE_DELAY_MS}
        onCancel={handleReset}
        onSaveNow={() => {
          void handleSave();
        }}
      />
      {/* Fixed Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, height: '100vh', zIndex: 100 }}>
        <Sidebar
          activeNav="settings"
          onSelectNav={(key) => {
            if (key === 'overview') navigate('/candidate');
            else navigate('/candidate', { state: { activeNav: key } });
          }}
          initials={initials}
          name={firstName.charAt(0).toUpperCase() + firstName.slice(1)}
          email={user?.email}
          organizationName={organizationName}
          departmentName={departmentName}
          onStartInterview={handleStartInterview}
          onLogout={handleLogout}
        />
      </div>

      {/* Main Content Area - offset by sidebar width */}
      <div style={{ marginLeft: '260px', flex: 1, minHeight: '100vh', position: 'relative' }}>

        {/* Dashboard Content */}
        <div className="dashboard-wrapper scrolled">
          <div className="profile-content">
            {/* Main Content Area */}
            <div className="profile-main-content" style={{ flex: 1, width: '100%' }}>
              <div className="overview-content">
                <div className="welcome-section">
                  <div className="welcome-left">
                    <h1>Settings</h1>
                    <p className="welcome-message">Customize how your AI interviews are conducted</p>
                  </div>
                </div>

          {/* Loading State */}
          {settingsState === 'loading' && (
            <div className="settings-loading">
              <div className="loading-spinner"></div>
              <p>Loading your settings...</p>
            </div>
          )}

          {/* Error State */}
          {settingsState === 'error' && (
            <div className="settings-error">
              <div className="error-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <h2>Failed to Load Settings</h2>
              <p>{errorMessage}</p>
              <button className="retry-button" onClick={handleRetry}>
                Try Again
              </button>
            </div>
          )}

          {/* Settings Content */}
          {(settingsState === 'ready' || settingsState === 'saving') && settingsData && (
            <>
              {/* Error Message */}
              {errorMessage && (
                <div className="settings-message error">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {errorMessage}
                </div>
              )}

              {/* Section 1: Mock Interview Settings */}
              <div className="settings-section">
                <div className="settings-section-header">
                  <h2 className="settings-section-title">Mock interview settings</h2>
                </div>

                {voiceAgentAvailable && (
                  <div className="settings-interviewer-card">
                    <div className="interviewer-card-header">
                      <span className="toggle-label">Interviewer model</span>
                      <span className="sidebar__badge sidebar__badge--new">new</span>
                    </div>
                    <div className="interviewer-radio-row" role="radiogroup" aria-label="Interviewer">
                      <label
                        className={`interviewer-radio-option${!useVoiceAgentEnabled ? ' selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="interviewer"
                          value="luci"
                          checked={!useVoiceAgentEnabled}
                          onChange={() => handleUseVoiceAgentChange(false)}
                        />
                        <span className="interviewer-radio-content">
                          <span className="interviewer-radio-name">Luci (Thinking)</span>
                          <span className="interviewer-radio-info">
                            Deep interview questions with longer response processing for high accuracy
                          </span>
                          <span className="interviewer-radio-meta">Voices and accents</span>
                        </span>
                      </label>
                      <label
                        className={`interviewer-radio-option${useVoiceAgentEnabled ? ' selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="interviewer"
                          value="andrea"
                          checked={useVoiceAgentEnabled}
                          onChange={() => handleUseVoiceAgentChange(true)}
                        />
                        <span className="interviewer-radio-content">
                          <span className="interviewer-radio-name">Andrea (fast)</span>
                          <span className="interviewer-radio-info">
                            Faster responses with thorough assessment on all the aspects
                          </span>
                          <span className="interviewer-radio-meta">No voice settings</span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                <div className="settings-knobs-grid">
                  {/* Difficulty Knob */}
                  <div className="settings-knob-card">
                    <SettingsKnob
                      label="Difficulty"
                      options={settingsData.valid_options.difficulty}
                      value={difficulty}
                      onChange={handleDifficultyChange}
                      displayLabels={difficultyLabels}
                    />
                    <p className="knob-description">
                      Set the complexity level of interview questions
                    </p>
                  </div>

                  {/* Experience Focus Knob */}
                  <div className="settings-knob-card">
                    <SettingsKnob
                      label="Experience Focus"
                      options={settingsData.valid_options.experience_focus}
                      value={experienceFocus}
                      onChange={handleExperienceFocusChange}
                      displayLabels={experienceFocusLabels}
                    />
                    <p className="knob-description">
                      Choose between varied topics or deep dive into recent experience
                    </p>
                  </div>

                  {/* Mode Knob */}
                  <div className="settings-knob-card">
                    <SettingsKnob
                      label="Interview Mode"
                      options={settingsData.valid_options.mode}
                      value={mode}
                      onChange={handleModeChange}
                      displayLabels={modeLabels}
                    />
                    <p className="knob-description">
                      Select the type of interview questions
                    </p>
                  </div>
                </div>

                {!useVoiceAgentEnabled && (
                  <div className="settings-interviewer-card">
                    <div className="interviewer-card-header">
                      <span className="toggle-label">Interviewer voice</span>
                    </div>
                    
                    <div className="interviewer-card-header">
                      <span className="toggle-label">Accent</span>
                    </div>
                    <div className="interviewer-radio-row" role="radiogroup" aria-label="Accent">
                      <label className={`interviewer-radio-option${accent === 'en-IN' ? ' selected' : ''}`}>
                        <input
                          type="radio"
                          name="accent"
                          value="en-IN"
                          checked={accent === 'en-IN'}
                          onChange={() => handleAccentChange('en-IN')}
                        />
                        <span className="interviewer-radio-content">
                          <span className="interviewer-radio-name">Indian English</span>
                          <span className="interviewer-radio-info">en-IN pronunciation</span>
                        </span>
                      </label>
                      <label className={`interviewer-radio-option${accent === 'en-US' ? ' selected' : ''}`}>
                        <input
                          type="radio"
                          name="accent"
                          value="en-US"
                          checked={accent === 'en-US'}
                          onChange={() => handleAccentChange('en-US')}
                        />
                        <span className="interviewer-radio-content">
                          <span className="interviewer-radio-name">American English</span>
                          <span className="interviewer-radio-info">en-US pronunciation</span>
                        </span>
                      </label>
                    </div>
                    <div className="interviewer-card-header" style={{ marginTop: '1.25rem' }}>
                      <span className="toggle-label">Interviewer</span>
                    </div>
                    <div className="interviewer-radio-row" role="radiogroup" aria-label="Interviewer voice">
                      <label
                        className={`interviewer-radio-option${interviewerGender === 'female' ? ' selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="interviewer_gender"
                          value="female"
                          checked={interviewerGender === 'female'}
                          onChange={() => handleInterviewerGenderChange('female')}
                        />
                        <span className="interviewer-radio-content">
                          <span className="interviewer-radio-name">{femaleInterviewerLabel}</span>
                          <span className="interviewer-radio-info">
                            {isTechnicalMode ? 'Technical interview voice' : 'Professional / behavioral voice'}
                          </span>
                        </span>
                      </label>
                      <label
                        className={`interviewer-radio-option${interviewerGender === 'male' ? ' selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="interviewer_gender"
                          value="male"
                          checked={interviewerGender === 'male'}
                          onChange={() => handleInterviewerGenderChange('male')}
                        />
                        <span className="interviewer-radio-content">
                          <span className="interviewer-radio-name">{maleInterviewerLabel}</span>
                          <span className="interviewer-radio-info">
                            {isTechnicalMode ? 'Technical interview voice' : 'Professional / behavioral voice'}
                          </span>
                        </span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Response Feedback Toggle */}
                <div className="settings-toggle-card">
                  <div className="toggle-header">
                    <span className="toggle-label">Enable response feedback</span>
                    <span className="toggle-tooltip" data-tooltip="Enable or disable feedback on how to answer for each response in a mock interview">?</span>
                  </div>
                  <Toggle
                    checked={responseFeedbackEnabled}
                    onChange={handleResponseFeedbackChange}
                  />
                </div>
              </div>

              {/* Section Separator */}
              <div className="settings-section-separator"></div>

              {/* Section 2: Vibe Gifs */}
              <div className="settings-section">
                <h2 className="settings-section-title">Vibe gifs</h2>
                
                <div className="settings-toggle-card">
                  <div className="toggle-header">
                    <span className="toggle-label">Enable vibe gifs</span>
                    <span className="toggle-tooltip" data-tooltip="Enable or disable the expressive gifs related to activities">?</span>
                  </div>
                  <Toggle
                    checked={vibeGifsEnabled}
                    onChange={handleVibeGifsChange}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="settings-actions">
                <button
                  className="reset-button"
                  onClick={handleReset}
                  disabled={!hasChanges || settingsState === 'saving'}
                >
                  Reset Changes
                </button>

                <div className="settings-actions-right">
                  {successMessage && (
                    <span className="settings-actions-message">{successMessage}</span>
                  )}
                </div>
              </div>

              {/* Profile Summary Card */}
              {settingsData.summary && (
                <div className="settings-profile-card">
                  <h3>Your Profile Summary</h3>
                  <p className="profile-summary">{settingsData.summary.replace(/\*\*/g, '')}</p>
                  {settingsData.resume_link && (
                    <a 
                      href={settingsData.resume_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="resume-link"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      View Resume
                    </a>
                  )}
                </div>
              )}
            </>
          )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
